/* eslint-disable no-return-await */
// eslint-disable-next-line no-use-before-define
import _cloneDeep from 'lodash/cloneDeep';
import _uniqBy from 'lodash/uniqBy';
import dynamic from 'next/dynamic';
import { StyleSheetManager } from 'styled-components';
import { iTHREE, Loader } from '@treedis/tour-loader';
import CssBaseline from '@mui/material/CssBaseline';
import rtlPlugin from 'stylis-plugin-rtl';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import socket from './util/socket';
import { checkIsFavourite } from '@/_helpers/base';
import * as sdk from './util/sdk';
import * as popups from './util/popups';
import { fetchData } from './util/all';
import { storage } from '@/_helpers/storage';
import PublicAPI from '@/api/TreedisPublicDAO';
// Components
import BaseTour from './tour/BaseTour';
import Header from './tour/eCommerce/header/Header';

import { TourType } from '@/_models/Tour';
import { Shipping } from '@/_models/eCommerce/Shipping';
import { eCommerceTheme, Product } from '@/_models/eCommerce/Product';
import { ExternalProvider } from '@/components/types';
import { DEFAULT_SORT } from '../components-new/eCommerce/Store/static';
import { AudioModule, MusicObjectKey } from '../components-new/common/AudioModule';
import { getLocationUrl } from './util/getLocationUrl';
import { sendToGA } from './util';
import { toast } from 'react-toastify';

const Products = dynamic(() => import('./tour/eCommerce/popups/Products'), { ssr: false });
const ProductPopup = dynamic(() => import('../components-new/eCommerce/ProductPopup'), {
  ssr: false,
});
const CartPopup = dynamic(() => import('../components-new/eCommerce/CartPopup'), { ssr: false });
const Checkout = dynamic(() => import('../components-new/eCommerce/Checkout'), { ssr: false });
const ContactMe = dynamic(() => import('../components-new/eCommerce/ContactMe'), { ssr: false });
const Store = dynamic(() => import('../components-new/eCommerce/Store'), { ssr: false });
const SuccessPurchaseScreen = dynamic(
  () => import('../components-new/eCommerce/SuccessPurchaseScreen'),
  { ssr: false },
);
const BottomBar = dynamic(() => import('../components-new/eCommerce/BottomBar'), { ssr: false });
const Sidebar = dynamic(() => import('../components-new/eCommerce/Sidebar'), { ssr: false });
const SoundControls = dynamic(() => import('./tour/regular/bottomBar/SoundControls'), {
  ssr: false,
});
const BbWhitelabelBlock = dynamic(
  () => import('../components-new/eCommerce/BottomBar/BbWhitelabelBlock'),
  { ssr: false },
);

const NEAREST_PRODUCTS_BY_CURRENT_POSITION = 5;
const NEARES_PRODUCTS_LIMIT = 15;
const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 100;
const TAG = 'tag';
const POLYGON = 'polygon';

const audioHandler = AudioModule.getInstance();

export default class ECommerceTour extends BaseTour {
  navigateToTag: any;

  preventTagAction: any;

  lastClickedItem: any = null;

  takeScreenshot = sdk.takeScreenshot.bind(this);

  takePano = sdk.takePano.bind(this);

  toggleContactPopup = popups.toggleContactPopup.bind(this);

  nearestProductsMatrix4: any;

  THREE: any;

  threeMatrix4: any;

  threeVector3: any;

  threeFrustum: any;

  isClickedToTag = false;

  constructor(props: any) {
    super(props, TourType.ECommerce);
    this.state = {
      ...this.state,
      currentProducts: null,
      cartOpen: false,
      checkoutOpen: false,
      contactMeOpen: false,
      contactMeIframeLink: null,
      contactMeRequiredFields: [],
      contactMeType: null,
      successPurchaseScreenOpen: false,
      storeOpen: false,
      cartProducts: [],
      menuFilterOptions: {
        vendors: [],
        options: [],
      },
      countries: new Map(),
      currency: {
        symbol: '$',
        code: 'USD',
      },
      shippingProfiles: [],
      favoriteProductsIds: [],
      favoriteProducts: [],
      allNearestProducts: [],
      menuProductsInfo: null,
      containerId: 'ecommerce',
      activeReel: null,
      settings: {},
      locations: [],
      externalProvider: null,
      pageSize: null,
      isLoadingProduct: false,
      isLoadingProducts: false,
      isActiveNearProducts: false,
      cameraPositionX: null,
      cameraPositionY: null,
      cameraPositionZ: null,
      isShowProducts: false,
      isShowProduct: false,
      productCountState: {},
      isFromCart: false,
      openedCartProductId: null,
    };

    this.navigateToTag = sdk.navigateToTag.bind(this);

    this.preventTagAction = sdk.preventTagAction.bind(this);
  }

  componentDidMount() {
    const { tour, matterId, tourData } = this.props;
    const url = getLocationUrl();
    if (!tourData?.tour) {
      toast.error('Tour not found');
      return;
    }
    // @ts-ignore
    window.tourId = tourData.tour.id;
    // @ts-ignore
    window.multiPath = [];

    if (tour) {
      // preview of the tour, load from props
      this.loadData(tour);
    } else {
      this.getTour({ slug: matterId, tourData, url });
    }
    this.getCountries();
    this.getProductCount();
    this.getMenuFilterOptions(matterId);
    this.getShipping(matterId);
    this.getSettings(matterId);
    this.getLocations(matterId);
    const favoriteProductsIds = storage('favoriteProductsIds').get() || {};
    const cartProducts = this.filterProducts(storage('cartProducts').get() || {});

    this.setState({
      favoriteProductsIds: favoriteProductsIds[matterId] || [],
      cartProducts: cartProducts[matterId] || [],
    });
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.takePosition);
    document.removeEventListener('contextmenu', this.disableContext);
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
    }
  }

  async getProductCount() {
    try {
      const {
        tourData: { tour },
      } = this.props;
      const response = await PublicAPI.getProductsCount({
        query: { tourId: tour.id },
      });
      this.setState({ productCountState: response.products });
    } catch (error) {
      console.error(error);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  filterProducts(products) {
    const newProducts = {};
    Object.keys(products).forEach((slug) => {
      const filteredProducts = (products[slug] || []).filter((product) => product?.id);
      newProducts[slug] = filteredProducts.length ? filteredProducts : [];
    });
    return newProducts;
  }

  async loadedShowcaseHandler() {
    await super.loadedShowcaseHandler(async () => {
      const {
        tour,
        externalProvider,
        isActiveNearProducts,
        isShowNearestProducts,
        cameraPositionX,
        cameraPositionY,
        cameraPositionZ,
      } = this.state;
      const isEcommerceTour = tour.mode === TourType.ECommerce;

      this.THREE = await iTHREE();
      this.threeMatrix4 = new this.THREE.Matrix4();
      this.threeFrustum = new this.THREE.Frustum();
      this.threeVector3 = new this.THREE.Vector3();
      const { x, y, z } = this.camera?.parent?.position || {};
      this.showcase.App.state.subscribe(async () => {
        if (
          isShowNearestProducts &&
          isActiveNearProducts &&
          x !== cameraPositionX &&
          y !== cameraPositionY &&
          z !== cameraPositionZ
        ) {
          await this.getAllNearestProduct();
        }
        if (
          externalProvider !== ExternalProvider.OPTICAL_CENTER &&
          isEcommerceTour &&
          isActiveNearProducts
        ) {
          await this.getNearestProducts();
        }
      });
      this.showcase.on(this.showcase.Mattertag.Event.CLICK, (tagSid: string) => {
        if (!this.isClickedToTag) {
          this.getProductsByTag(tagSid);
        }
      });
    });
  }

  // eslint-disable-next-line consistent-return
  async getAllNearestProduct() {
    try {
      if (this.camera && this.camera?.parent) {
        const {
          tourData: { tour },
        } = this.props;
        const { settings } = this.state;
        const { x, y, z } = this.camera?.parent?.position || {};
        const query: any = {
          positionX: x,
          positionY: y,
          positionZ: z,
          radius: NEAREST_PRODUCTS_BY_CURRENT_POSITION,
          tourId: tour?.id,
          userId: tour?.userId,
        };
        if (settings?.masterTourId) {
          query.masterTourId = settings?.masterTourId;
        }

        const response = await PublicAPI.getNearestProducts({
          query,
        });

        if (response && response?.products) {
          this.setState({
            allNearestProducts: response?.products || [],
            cameraPositionX: x,
            cameraPositionY: y,
            cameraPositionZ: z,
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  openProductPopup(type: string, id: number | string) {
    let foundCountOfProduct;
    const { productCountState, allTags } = this.state;
    const foundTag = Array.from(allTags).find(
      ([, item]) => item.sid === id || item.defaultSid === id,
    );
    // @ts-ignore
    if (type === TAG && foundTag?.length === 2) {
      foundCountOfProduct = +productCountState.tags.find(
        (item) => item.sid === foundTag[1].defaultSid,
      )?.count;
    }
    if (type === POLYGON) {
      foundCountOfProduct = +productCountState.polygons.find((item) => item.id === id)?.count;
    }
    if (foundCountOfProduct > 1) {
      this.setState({ isShowProducts: true });
    }
    if (foundCountOfProduct === 1) {
      this.setState({ isShowProduct: true });
    }
  }

  async getProductsByTag(tagSid: string) {
    this.isClickedToTag = true;
    try {
      this.openProductPopup(TAG, tagSid);
      this.setState({ isLoadingProduct: true });
      const { tour, settings } = this.state;
      this.lastClickedItem = null;
      let products = [];
      const tagsArray = Array.from(this.state.allTags);
      const tag =
        tagsArray
          .map(([, item]) => item)
          .find((item) => item.sid === tagSid || item.defaultSid === tagSid) || null;
      const result = await PublicAPI.getProductsByTag({
        query: {
          tourId: tour?.id,
          userId: tour?.userId,
          masterTourId: settings?.masterTourId,
          tagSid: tag?.defaultSid || tag?.sid,
        },
      });
      if (result && result?.data) {
        products = result.data.map((product) => new Product(product, tour?.userId));
      }
      if (products && products.length > 0) {
        this.hideTreedisTagTooltip(undefined, true);
        this.lastClickedItem = tag;
        this.setProductsStateWithDelay(products);
        window.location.hash = tagSid;
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isClickedToTag = false;
      this.setState({ isLoadingProduct: false });
    }
  }

  setProductsStateWithDelay = (products) => {
    setTimeout(
      () =>
        this.setState({
          currentProducts: products.length > 1 ? products : [],
          currentProduct: products.length === 1 ? products[0] : null,
        }),
      400,
    );
  };

  // eslint-disable-next-line consistent-return
  async getContaintsPoint(element) {
    const { positionX, positionY, positionZ } = element;
    this.threeVector3.set(positionX, positionY, positionZ);
    if ((await this.getDistanceToPoint(element)) <= NEAREST_PRODUCTS_BY_CURRENT_POSITION) {
      return this.threeFrustum.containsPoint(this.threeVector3);
    }
  }

  async getNearestProducts() {
    const { externalProvider, allTags, tour, allNearestProducts } = this.state;
    if (externalProvider === ExternalProvider.OPTICAL_CENTER) return;
    const allTagsArray = Array.from(allTags.values());
    const nearestProducts = [];
    if (this.camera && allNearestProducts.length) {
      this.threeFrustum.setFromProjectionMatrix(
        this.threeMatrix4.multiplyMatrices(
          this.camera.projectionMatrix,
          this.camera.matrixWorldInverse,
        ),
      );
      // eslint-disable-next-line no-restricted-syntax
      for await (const product of allNearestProducts) {
        const productTagSid = product.tags?.[0]?.sid;
        if (productTagSid) {
          const tag = allTagsArray.find(
            (item: any) => item?.sid === productTagSid || item?.defaultSid === productTagSid,
          );
          if (tag) {
            const containsPoint = await this.getContaintsPoint(tag);
            if (containsPoint) {
              nearestProducts.push(product);
            }
          }
        }
        if (nearestProducts.length <= NEARES_PRODUCTS_LIMIT && product.polygons?.length) {
          // eslint-disable-next-line no-restricted-syntax
          for await (const polygon of product.polygons) {
            const containsPoint = await this.getContaintsPoint(polygon);
            if (containsPoint) {
              nearestProducts.push(product);
            }
          }
        }
      }
    }
    const transformProduct = nearestProducts.map((item) => new Product(item, tour?.userId));
    this.setState({
      nearestProducts: _uniqBy(transformProduct, 'id'),
    });
  }

  startVideo = async () => {
    audioHandler.pauseAllMusic();
    // if tourMusic was playing - pause it
    this.setState({ videoPlaying: true });
  };

  stopVideo = async () => {
    const { videoResume } = this.state;
    if (videoResume) {
      await videoResume.play();
    }
    this.setState({ videoPlaying: false });
  };

  async getDistanceToPoint(item) {
    return await this.getDistanceTwoPoint(this.pose.position, {
      x: item.positionX,
      y: item.positionY,
      z: item.positionZ,
    });
  }

  getCountries = () => {
    fetchData('public/countries/getAll', 'GET').then((data) => {
      if (data.countries) {
        const { countries } = this.state;
        data.countries.map((country) => countries.set(country.id, country.nicename));
        this.setState({ countries });
      }
    });
  };

  getSettings = (matterId) => {
    fetchData('public/eCommerce/settings', 'GET', {
      params: {
        matterId,
      },
    }).then(({ status, data }) => {
      if (status === 200) {
        this.setState({
          currency: data.currency,
          activeReel: data.activeReel,
          isOpenReel: data.isOpenReel,
          settings: data,
          contactMeType: data.contactMeType,
          contactMeIframeLink: data.contactMeIframeLink,
          contactMeRequiredFields: data.contactMeRequiredFields,
          externalProvider: data.externalProvider,
        });
      }
    });
  };

  getLocations = async (matterId) => {
    const res = await PublicAPI.getLocationsByTour({
      query: {
        matterId,
      },
    });

    if (res && res.data) {
      this.setState({
        locations: res.data,
      });
    }
  };

  getShipping = (matterId: string) => {
    fetchData('public/eCommerce/shipping/getAll', 'GET', {
      params: {
        matterId,
      },
    }).then((data) => {
      if (data.shippingProfiles) {
        this.setState({
          shippingProfiles: data.shippingProfiles.map((profile) => new Shipping(profile)),
        });
      }
    });
  };

  getProductsWithPagination = async (options: any) => {
    try {
      this.setState({ isLoadingProducts: true });
      const { userId, tourId, productIds } = options;
      if (!userId || !tourId) {
        return;
      }
      if (options.likedOption === true && productIds === '[]') {
        this.setState({ products: [] });
        return;
      }
      return await PublicAPI.getProductsWithPagination({
        query: {
          ...options,
        },
      });
    } catch (error) {
      console.warn(error);
    } finally {
      this.setState({ isLoadingProducts: false });
    }
  };

  setProductsWithPagination = async (options: any) => {
    const response = await this.getProductsWithPagination(options);
    if (response && response?.data) {
      this.setState({
        products: response.data?.data.map((product) => new Product(product, options.userId)) || [],
        pageSize: response?.data?.pageSize,
      });
    }
  };

  getFavoriteProductsFromApi = async () => {
    const { tour, settings, favoriteProductsIds } = this.state;
    try {
      if (favoriteProductsIds.length) {
        const response = await this.getProductsWithPagination({
          tourId: tour?.id,
          userId: tour?.userId,
          masterTourId: settings?.masterTourId,
          sortByName: DEFAULT_SORT,
          sortByDesc: true,
          page: DEFAULT_PAGE,
          limit: DEFAULT_LIMIT,
          productIds: JSON.stringify(favoriteProductsIds),
        });
        if (response?.data) {
          const products = response?.data.data?.map(
            (product) => new Product(product, tour?.userId),
          );
          this.setState({ favoriteProducts: products });
        }
      } else {
        this.setState({ favoriteProducts: [] });
      }
    } catch (error) {
      console.error(error);
    }
  };

  getMenuFilterOptions = async (matterId: any) => {
    if (!matterId) return;
    fetchData('public/eCommerce/products/getFilterOptions', 'GET', {
      params: {
        matterId,
        optionId: 2,
      },
    })
      .then((data) =>
        this.setState({
          menuFilterOptions: {
            options: data.options,
            vendors: (data.vendors || []).filter((vendor) => vendor.countProducts > 0),
          },
        }),
      )
      .catch((err) => {
        console.error(err.message);
      });
  };

  onAddToCart = (variant) => {
    const { matterId } = this.props;
    let cartProducts = _cloneDeep(this.state.cartProducts) || [];
    if (cartProducts.some((item) => variant.id === item.id)) {
      if (variant.isReplace) {
        cartProducts = cartProducts.filter((item) => variant.id !== item.id);
        cartProducts.push(variant);
      } else {
        cartProducts = cartProducts.map((item) => ({
          ...item,
          quantity: variant.id === item.id ? variant.quantity : item.quantity,
        }));
      }
    } else {
      cartProducts.push(variant);
    }
    storage('cartProducts').set({
      ...((storage('cartProducts').get() as Record<string, unknown>) || {}),
      [matterId]: cartProducts,
    });
    this.setState({
      cartProducts,
    });
  };

  onChangeCart = (productId, quantity) => {
    const { matterId } = this.props;
    const prevProducts = _cloneDeep(this.state.cartProducts) || [];
    const cartProducts = prevProducts
      .filter((item) => productId !== item.id || (quantity !== 0 && productId === item.id))
      .map((item: any) => ({
        ...item,
        quantity: productId === item.id ? quantity : item.quantity,
      }));
    storage('cartProducts').set({
      ...((storage('cartProducts').get() as Record<string, unknown>) || {}),
      [matterId]: cartProducts,
    });

    this.setState({
      cartProducts,
    });
  };

  // CALLBACKS TO AVOID RENDERS
  openCart = () => this.setState({ cartOpen: true });

  openCartAndCloseModal = () => {
    this.setState({ cartOpen: true, currentProduct: null });
  };

  openShare = () => this.setState(({ sharePopupOn }) => ({ sharePopupOn: !sharePopupOn }));

  openCheckout = () => this.setState({ checkoutOpen: true });

  openContactMe = () => this.setState({ contactMeOpen: true });

  openSuccessPurchaseScreen = () => {
    this.setState({
      cartOpen: false,
      checkoutOpen: false,
      successPurchaseScreenOpen: true,
    });
  };

  openStore = () => this.setState({ storeOpen: true });

  backCheckout = () => this.setState({ checkoutOpen: false });

  closeCart = () => this.setState({ cartOpen: false });

  closeCheckout = () => {
    this.setState({ cartOpen: false });
    this.setState({ checkoutOpen: false });
  };

  closeContactMe = () => this.setState({ contactMeOpen: false });

  closeProductPopup = () => {
    this.setState({
      currentProduct: false,
      isShowProduct: false,
      isFromCart: false,
      openedCartProductId: null,
    });
  };

  closeProductsPopup = () => {
    this.setState({ currentProducts: false, isShowProducts: false });
  };

  closeSuccessPurchaseScreen = () => this.setState({ successPurchaseScreenOpen: false });

  closeStore = () => this.setState({ storeOpen: false });

  clearCart = () => {
    const { matterId } = this.props;
    // const { tour } = this.state;
    // this.getTour({ id: matterId });
    // this.loadData(tour);
    const storageCartProducts = (storage('cartProducts').get() as Record<string, unknown>) || {};
    storage('cartProducts').set(
      Object.keys(storageCartProducts).reduce((acc, key) => {
        if (key !== matterId) acc[key] = storageCartProducts[key];
        return acc;
      }, {}),
    );

    this.setState({
      cartProducts: [],
    });
  };

  handleSoundControlClose = () => {
    this.setState({
      otherPlaying: false,
    });
  };

  showMinimapClick = () =>
    this.setState(({ minimapVisible }) => ({ minimapVisible: !minimapVisible }));

  showPanoramaClick = () =>
    this.setState(({ panoramaOn }) => ({ panoramaOn: !panoramaOn, panoramaStart: false }));

  onHelpClick = () => this.setState({ showHelpModal: true, showTermsModal: false });

  onTermsClick = () => this.setState({ showHelpModal: false, showTermsModal: true });

  getSetting = async (setting: string) => {
    try {
      return await this.showcase?.Settings?.get(setting);
    } catch (err) {
      console.error(err);
    }
    return null;
  };

  toggleGameBook = () => {
    this.setState(({ showBook }) => ({ showBook: !showBook }));
  };

  toggleMenu = () => {
    this.setState(({ menuOpen }) => ({ menuOpen: !menuOpen }));
  };

  handleMeasureClick = () => {
    if (this.state.panoramaOn) this.showPanoramaClick();
    this.measurementsMode();
  };
  // CALLBACKS TO AVOID RENDERS END

  renderBottomBar(isFullScreen, toggleFullscreen) {
    const {
      favoriteProductsIds,
      favoriteProducts,
      nearestProducts,
      currency,
      cartProducts,
      translation,
      activeSweepGroup,
      tour,
      panoramaOn,
      settings,
      reel,
      reelStep,
      currentFloor,
      mpFloors,
      chatToken,
      activeReel,
      isOpenReel,
      tourMode,
      tourPlaying,
      minimapVisible,
      videoPlaying,
      currentSweep,
      clickedTag,
    } = this.state;

    const firstAudio = audioHandler.getFirstMusic();
    let audio;
    if (firstAudio?.type.name === MusicObjectKey.TAGS) {
      audio = firstAudio.audio;
    }

    return (
      <>
        {clickedTag && audio && !audio?.src?.includes('/sweeps/') && (
          <SoundControls audio={audio} onClose={this.handleSoundControlClose} flowIsActive />
        )}
        {!this.state.showIntro && !this.state.startScreenOn && (
          <BottomBar
            translation={translation}
            isFull={isFullScreen}
            changeFull={toggleFullscreen}
            tourMode={tourMode}
            loadModel={this.loadModel}
            tour={tour}
            playTour={this.playTour}
            tourPlaying={tourPlaying}
            changeViewMode={this.changeViewMode}
            panoramaOn={panoramaOn}
            moveToStep={this.moveToStep}
            changeFloor={this.changeFloor}
            showPanorama={tour.panorama && !chatToken}
            toggleExternalPopup={this.toggleExternalPopup}
            showPanoramaClick={this.showPanoramaClick}
            cartProducts={cartProducts}
            nearestProducts={nearestProducts}
            openProduct={this.openProduct}
            openVR={this.openVR}
            currentFloor={currentFloor}
            currency={currency}
            activeReel={activeReel}
            isOpenReel={isOpenReel}
            reel={reel}
            reelStep={reelStep}
            activeSweepGroup={activeSweepGroup}
            mpFloors={mpFloors}
            favoriteProductsIds={favoriteProductsIds}
            favoriteProducts={favoriteProducts}
            onChangeFavourite={this.onChangeToFavorite}
            getSetting={this.getSetting}
            openCart={this.openCart}
            openContactMe={this.openContactMe}
            theme={settings?.theme}
            onStopVideo={this.stopVideo}
            showMinimapClick={this.showMinimapClick}
            videoPlaying={videoPlaying}
            minimapVisible={minimapVisible}
            isOpticalCenter={this.state.externalProvider === ExternalProvider.OPTICAL_CENTER}
            settings={settings}
            handleMeasureClick={this.handleMeasureClick}
            currentSweep={currentSweep}
            handleNearProductStatus={(state: false) =>
              this.setState({ isActiveNearProducts: state })
            }
            getFavoriteProductsFromApi={this.getFavoriteProductsFromApi}
            handleProductIdForPopup={(state, id) =>
              this.setState({
                isFromCart: state,
                openedCartProductId: id,
              })
            }
          />
        )}
      </>
    );
  }

  openProduct = async (initialProduct) => {
    this.setState({ isShowProduct: true, isShowProducts: false });
    const product = { ...initialProduct };

    if (product) {
      const tagSid = product.tags?.[0]?.sid;
      if (tagSid) {
        try {
          await this.navigateToTag(tagSid);
          this.showcase.Tag.close(tagSid);
        } catch (error) {
          console.error('Navigation failed:', error);
        }
      }

      this.setState({
        currentProduct: product,
      });
    } else {
      console.error('Product select error ecommerceTour => openProduct', initialProduct, product);
    }
  };

  openProductWithDelay = (initialProduct) => {
    this.setState({ isShowProducts: false });
    const product = { ...initialProduct };
    if (product) {
      this.setState({ isShowProduct: false, currentProduct: null }); // delay to unmount compoenent
      setTimeout(() => {
        this.setState({ currentProduct: product, isShowProduct: true });
      }, 100);
    }
  };

  async handlePolygonProduct(polygon) {
    try {
      this.openProductPopup(POLYGON, polygon?.id);
      this.setState({ isLoadingProduct: true });
      let products = [];
      const { tour, settings } = this.state;
      const result = await PublicAPI.getProductsByPolygon({
        query: {
          tourId: tour?.id,
          userId: tour?.userId,
          masterTourId: settings?.masterTourId,
          polygonId: polygon?.id,
        },
      });
      if (result && result?.data) {
        products = result.data.map((product) => new Product(product, tour?.userId));
      }
      this.lastClickedItem = polygon;
      this.setProductsStateWithDelay(products);
    } catch (error) {
      console.error(error);
    } finally {
      this.setState({ isLoadingProduct: false });
    }
  }

  handleVSProduct(vsObject: any) {
    const products =
      vsObject?.products.map((item) => new Product(item, this.state.tour.userId)) || [];
    if (products.length > 1) {
      this.setState({ isShowProducts: true });
    }
    if (products.length === 1) {
      this.setState({ isShowProduct: true });
    }
    this.setState({
      currentProducts: products.length > 1 ? products : [],
      currentProduct: products.length === 1 ? products[0] : null,
    });
  }

  onChangeToFavorite = (product?: Product, isMenu = true) => {
    const { currentProduct } = this.state;
    const targetProduct = product || currentProduct;
    if (checkIsFavourite(this.state.favoriteProductsIds, targetProduct)) {
      this.onDeleteFromFavorite(targetProduct);
      return;
    }
    this.onAddToFavorite(targetProduct);
    sendToGA('event', {
      eventCategory: isMenu ? 'vCommerceLikeMenu' : 'vCommerceLike',
      eventAction: 'click',
      eventLabel: targetProduct?.title,
      eventId: targetProduct.id,
    });
  };

  onAddToFavorite(product) {
    const { matterId } = this.props;
    const { favoriteProducts } = this.state;
    const favoriteProductsIds = _cloneDeep(this.state.favoriteProductsIds) || [];
    favoriteProductsIds.push(product?.id);
    storage('favoriteProductsIds').set({
      ...((storage('favoriteProductsIds').get() as Record<string, unknown>) || {}),
      [matterId]: favoriteProductsIds,
    });
    this.setState({
      favoriteProductsIds,
      favoriteProducts: [...favoriteProducts, product],
    });
  }

  onDeleteFromFavorite(product) {
    const { matterId } = this.props;
    const { favoriteProducts } = this.state;
    const favoriteProductsIds = _cloneDeep(this.state.favoriteProductsIds) || [];
    const productsIds = favoriteProductsIds.filter(
      (favoriteProductId) => favoriteProductId !== product.id,
    );
    storage('favoriteProductsIds').set({
      ...((storage('favoriteProductsIds').get() as Record<string, unknown>) || {}),
      [matterId]: productsIds,
    });

    this.setState({
      favoriteProductsIds: productsIds,
      favoriteProducts: favoriteProducts.filter((item) => item.id !== product.id),
    });
  }

  // eslint-disable-next-line class-methods-use-this
  async getDistanceTwoPoint(originalPosition, targetPosition) {
    const THREE = await iTHREE();

    const cameraDistance = new THREE.Vector3();
    const target = new THREE.Vector3(targetPosition.x, targetPosition.y, targetPosition.z);
    const original = new THREE.Vector3(originalPosition.x, originalPosition.y, originalPosition.z);
    cameraDistance.subVectors(original, target);
    return cameraDistance.length();
  }

  injectChildComponents() {
    const {
      tour,
      panoramaOn,
      chatToken,
      whiteLabelData,
      currentProducts,
      cartOpen,
      checkoutOpen,
      contactMeOpen,
      successPurchaseScreenOpen,
      storeOpen,
      translation,
      currency,
      products,
      tourEmail,
      mainPlaying,
      currentFloor,
      sharePopupOn,
      isVSEnabled,
      tourType,
      settings,
      minimapVisible,
      game,
      showBook,
      menuOpen,
      cartProducts,
      showcaseLoaded,
      isShowProducts,
      isShowProduct,
      isFromCart,
      openedCartProductId,
    } = this.state;
    const theme = createTheme({
      direction: tour?.client?.rtl ? 'rtl' : undefined,
      // @ts-ignore
      palette: {
        mode: settings?.theme || eCommerceTheme.light,
      },
    });
    const isHasFloorMusic = tour.musics?.find((item) => item.floor === currentFloor + 1);
    return (
      // @ts-ignore
      <StyleSheetManager stylisPlugins={[...(tour?.client?.rtl ? [rtlPlugin] : [])]}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {(this.state.isLoadingProducts || this.state.isLoadingProduct) && (
            <div
              style={{
                marginTop: 'calc(50vh - 50px)',
                marginLeft: 'calc(50vw - 50px)',
                width: '1px',
                height: '1px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundClip: 'unset',
                position: 'absolute',
                transform: this.state.isLoadingProducts ? 'rotateX(180deg)' : undefined,
              }}
            >
              <Loader />
            </div>
          )}
          {this.isTourReadyForAudio() && showcaseLoaded && (
            <Sidebar
              showPathSwitch={game?.paths?.length > 1}
              whiteLabelData={whiteLabelData}
              onHelpClick={this.onHelpClick}
              contactPhone={this.state.tourEmail?.contactPhone}
              menuVisible={menuOpen}
              toggleMenu={this.toggleMenu}
              gameBookVisible={showBook}
              showGameBook={game?.gameBookOn && game?.book}
              panoramaOn={panoramaOn}
              isTourReadyForAudio={this.isTourReadyForAudio}
              minimapVisible={minimapVisible}
              vsObjects={this.vsObjects}
              isVSEnabled={isVSEnabled}
              toggleVS={this.toggleVS}
              isHasFloorMusic={isHasFloorMusic}
              mainPlaying={mainPlaying}
              translation={translation}
              tourEmail={tourEmail}
              tour={tour}
              sharePopupOn={sharePopupOn}
              tourType={tourType}
              cartProducts={cartProducts}
              showMinimapClick={this.showMinimapClick}
              toggleContactPopup={this.toggleContactPopup}
              toggleAppointmentsModal={this.toggleAppointmentsModal}
              openCart={this.openCart}
              openStore={this.openStore}
              openShare={this.openShare}
              onStartVideo={this.startVideo}
              onStopVideo={this.stopVideo}
              takeScreenshot={this.takeScreenshot}
              handleToggleMusic={this.handleToggleMusic}
              getSetting={this.getSetting}
              toggleGameBook={this.toggleGameBook}
              menuWidth={this.state.menuWidth}
            />
          )}
          {!isShowProduct &&
            !isShowProducts &&
            !panoramaOn &&
            !cartOpen &&
            !checkoutOpen &&
            !contactMeOpen &&
            !successPurchaseScreenOpen &&
            storeOpen && (
              <Store
                settings={settings}
                rtl={tour?.client?.rtl}
                tourId={tour?.id}
                userId={tour?.userId}
                productTypes={this?.state?.productTypes}
                onChangeFavourite={this.onChangeToFavorite}
                favoriteProductsIds={this.state.favoriteProductsIds}
                products={products}
                filterOptions={this.state.menuFilterOptions}
                currency={currency}
                translation={translation}
                onClose={this.closeStore}
                openProduct={this.openProduct}
                setMenuData={(menusData) => this.setState({ ...menusData })}
                getProductsWithPagination={this.setProductsWithPagination}
                storeProducts={this.state.products}
                pageSize={this.state.pageSize}
                isLoadingProducts={this.state.isLoadingProducts}
              />
            )}
          {!panoramaOn && isShowProduct && (
            <ProductPopup
              panoramaOn={panoramaOn}
              isLoadingProduct={this.state.isLoadingProduct}
              closeProductPopup={this.closeProductPopup}
              rtl={tour.client && tour.client.rtl}
              currentProduct={this.state.currentProduct}
              whiteLabel={whiteLabelData}
              logo={
                tour?.client?.withLogo
                  ? `${process.env.NEXT_PUBLIC_CDN}/clients/${tour.client.id}/logo.png`
                  : null
              }
              cartProducts={cartProducts}
              onAddToCart={this.onAddToCart}
              openCart={this.openCartAndCloseModal}
              openProduct={this.openProductWithDelay}
              onChangeFavorite={this.onChangeToFavorite}
              favoriteProductsIds={this.state.favoriteProductsIds}
              currency={currency}
              translation={translation}
              settings={settings}
              userId={tour.userId}
              tourId={tour.id}
              onChangeCart={this.onChangeCart}
              handleLoadingProduct={(state: boolean) => this.setState({ isLoadingProduct: state })}
              isFromCart={isFromCart}
              openedCartProductId={openedCartProductId}
              isShowProduct={isShowProduct}
            />
          )}

          {!contactMeOpen && !checkoutOpen && cartOpen && (
            <CartPopup
              whitelabel={whiteLabelData}
              matterId={tour.matterId}
              client={tour.client}
              cartProducts={cartProducts}
              shippingProfiles={this.state.shippingProfiles}
              countries={this.state.countries}
              rtl={tour?.client?.rtl}
              currency={currency}
              translation={translation}
              tour={tour}
              settings={settings}
              locations={this.state.locations}
              favoriteProductsIds={this.state.favoriteProductsIds}
              onClose={this.closeCart}
              onChangeCart={this.onChangeCart}
              clearCart={this.clearCart}
              openCheckout={this.openCheckout}
              openContactMe={this.openContactMe}
            />
          )}

          {!contactMeOpen && checkoutOpen && (
            <Checkout
              tourSlug={tour?.slug}
              rtl={tour?.client?.rtl}
              clientPaypalId={settings.paypalClientId || tour?.client?.paypalClientId}
              countries={this.state.countries}
              currency={currency}
              locations={this.state.locations}
              cartProducts={cartProducts}
              translation={translation}
              settings={settings}
              onClose={this.closeCheckout}
              onBack={this.backCheckout}
              openContactMe={this.openContactMe}
              clearOrder={this.clearCart}
              openSuccessPurchaseScreen={this.openSuccessPurchaseScreen}
            />
          )}
          {contactMeOpen && (
            <ContactMe
              settings={settings}
              rtl={tour?.client?.rtl}
              currency={currency}
              tourSlug={tour?.slug}
              translation={translation}
              countries={this.state.countries}
              cartProducts={cartProducts}
              onClose={this.closeContactMe}
              contactMeType={this.state.contactMeType}
              contactMeIframeLink={this.state.contactMeIframeLink}
              contactMeRequiredFields={this.state.contactMeRequiredFields}
            />
          )}
          {successPurchaseScreenOpen && (
            <SuccessPurchaseScreen
              translation={translation}
              closeSuccessPurchaseScreen={this.closeSuccessPurchaseScreen}
            />
          )}

          <Header
            tour={tour}
            translation={translation}
            logoUrl={this.getLogoUrl()}
            chatToken={chatToken}
          />

          {isShowProducts && !panoramaOn && (
            <Products
              rtl={tour.client && tour.client.rtl}
              theme={settings?.theme}
              onClose={this.closeProductsPopup}
              products={currentProducts}
              lastClickedItem={this.lastClickedItem}
              onClick={(product) =>
                this.setState({
                  currentProduct: product,
                })
              }
              openProduct={this.openProduct}
              onChangeFavourite={this.onChangeToFavorite}
              favoriteProductsIds={this.state.favoriteProductsIds}
              currency={currency}
            />
          )}
          {tour.hideSidebarConfigs && (
            <BbWhitelabelBlock
              translation={translation}
              whiteLabel={whiteLabelData}
              tour={tour}
              onHelpClick={this.onHelpClick}
            />
          )}
        </ThemeProvider>
      </StyleSheetManager>
    );
  }
}
